import { ControlFlagDocIdvRequiredAndCompletedControlValues } from '@getethos/ethos-global-lib';
import { TaskTypes } from '../features/tasks/types/taskTypes';
import { ApiEndPoints } from './interfaces/IApiEndPoints';

/**
 * place for maintaining all api endpoints
 */
export const apiEndPoints: ApiEndPoints = {
  decisionTypes: 'metadata/decisiontypes',
  applicationStage: 'metadata/applicationstage',
  applicationLifecycle: 'metadata/applicationlifecycle',
  applicationDetailedLifecycle: 'metadata/applicationdetailedlifecycle',
  rateClasses: 'metadata/rateclasses',
  tobaccoClasses: 'metadata/tobaccoclasses',
  waiveReasons: 'metadata/waivereasons',
  pa: 'metadata/pa',
  underwriters: 'users/underwriters',
  carrierunderwriters: 'users/carrierunderwriters',
  caseManagers: 'users/casemanagers',
  uwxUsers: 'users/uwxusers',
  referrals: 'users/referrals',
  applications: 'applications',
  users: 'users',
  clientConfig: 'metadata/clientConfig',
  createUser: 'users',
  acceptTnc: 'users/tnc',
  automatedEvidences: 'automatedEvidences',
  admin: 'admin',
  reassign: 'admin/reassign',
  reassignToUsers: 'admin/reassigntousers',
  bulkReassignByUser: 'admin/bulk-reassign-by-user',
  bulkCaseAssignmentValidate: 'admin/bulk-case-assignment/validate',
  bulkCaseAssignmentExecute: 'admin/bulk-case-assignment/execute',
  history: 'admin/history?count=50',
  userAssignmentCount: 'applications/assignment/count',
  erroredTasks: 'task/errored',
  document: 'document',
  declineConditions: 'metadata/declinecondition',
  taskCounts: 'task/taskCounts',
  millimanPortal: 'task/millimanPortal',
  attachment: 'attachments',
  cleanPia: 'admin/job/autoPia/cleanPolicies',
  planFUpload: 'admin/job/planF/upload',
  planFUploads: 'admin/job/planF/uploads',
  digitalOwlUpload: 'admin/job/digitalOwl/upload',
  digitalOwlUploads: 'admin/job/digitalOwl/uploads',
  eti: 'eti/admin',
  postponeConditions: 'metadata/postponecondition',
  managerTabExceptionQueue: 'admin/manager-tab/exception-queue',
  managerTabTeamCapacity: 'admin/manager-tab/team-capacity',
  managerTabSmartRetry: 'admin/manager-tab/smart-retry',
  managerTabEligibleUws: 'admin/manager-tab/eligible-uws',
  managerTabSpecializationOptions: 'admin/manager-tab/specialization-options',
  managerTabBulkCaseAssignment: 'admin/manager-tab/bulk-case-assignment',
  managerTabBulkUserReassignment: 'admin/manager-tab/bulk-user-reassignment',
};

export const deleteDocumentEndPoint = (id: number): string => {
  return `${apiEndPoints.document}/${id}/delete`;
};

export const getTaskEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/task`;
};
export const getRequestRuleReviewEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/task/ruleReview`;
};
export const getTaskUpdateEndPoint = (id: string, tid: number): string => {
  return `${apiEndPoints.applications}/${id}/task/${tid}`;
};
export const getMarkTaskOutdatedEndpoint = (
  id: string,
  tid: number,
): string => {
  return `${apiEndPoints.applications}/${id}/task/${tid}/outdated`;
};

export const getApplicationEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}`;
};

export const getReferralsListEndPoint = (applicationId: string): string => {
  return `${apiEndPoints.applications}/${applicationId}/referrals`;
};

export const createReferralEndPoint = (applicationId: string): string => {
  return `${apiEndPoints.applications}/${applicationId}/referral`;
};

export const closeReferralEndPoint = (
  applicationId: string,
  referralId: number,
): string => {
  return `${apiEndPoints.applications}/${applicationId}/referral/${referralId}/close`;
};

export const referralResponseEndPoint = (
  applicationId: string,
  referralId: number,
): string => {
  return `${apiEndPoints.applications}/${applicationId}/referral/${referralId}/response`;
};

export const referralFollowUpEndPoint = (
  applicationId: string,
  referralId: number,
): string => {
  return `${apiEndPoints.applications}/${applicationId}/referral/${referralId}/follow-up`;
};

export const getReferralActionRequiredEndPoint = (): string => {
  return 'referrals/action-required';
};

export const getReferralActionRequiredCountEndPoint = (): string => {
  return 'referrals/action-required/count';
};

export const getApplicationPIIDataEndPoint = (
  applicationId: number,
  termPolicyId: number,
): string => {
  return `${apiEndPoints.applications}/${applicationId}/policy/${termPolicyId}/piiData`;
};

export const getIdentificationDocLinkEndPoint = (
  applicationId: number,
  controlValue: ControlFlagDocIdvRequiredAndCompletedControlValues,
): string => {
  return `${apiEndPoints.applications}/${applicationId}/identificationDocLink?controlValue=${controlValue}`;
};

export const getNotesEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/notes`;
};

export const deleteNoteEndPoint = (id: string, noteId: string): string => {
  return `${apiEndPoints.applications}/${id}/notes/${noteId}/delete`;
};

export const getDraftEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/notes/draft`;
};

export const getPiaInfoEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/pia-info`;
};

export const getRuleResultsEndPoint = (
  id: string,
  resultStatusType: string,
): string => {
  return `${apiEndPoints.applications}/${id}/decision/ruleResults?resultStatusType=${resultStatusType}`;
};

export const getReasonsEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/reasons`;
};

export const getExistingPoliciesEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/existingpolicies`;
};

export const getOwnerLinkedPoliciesEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/ownerlinkedpolicies`;
};

export const getOwnerDependentPoliciesEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/ownerdependentpolicies`;
};

export const getOwnerProfileEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/ownerprofile`;
};

export const uploadEvidenceEndPoint = (
  applicationId: string,
  taskId: number,
) => {
  return `/${apiEndPoints.applications}/${applicationId}/task/${taskId}/evidence`;
};

export const createCarrierDecision = (applicationId: string) => {
  return `${apiEndPoints.applications}/${applicationId}/decision/createCarrierDecision`;
};

export const applicationDecisionEndPoint = (applicationId: string) => {
  return `${apiEndPoints.applications}/${applicationId}/decision`;
};

export const applicationDecisionV2EndPoint = (applicationId: string) => {
  return `${apiEndPoints.applications}/${applicationId}/decision/v2`;
};

export const applicationDecisionEditEndPoint = (
  applicationId: string,
  decisionId: string,
) => {
  return `${apiEndPoints.applications}/${applicationId}/decision/${decisionId}`;
};

export const applicationDecisionEditV2EndPoint = (
  applicationId: string,
  decisionId: string,
) => {
  return `${apiEndPoints.applications}/${applicationId}/decision/${decisionId}/v2`;
};

export const applicationDecisionLeadingToCLREditEndPoint = (
  applicationId: string,
  decisionId: string,
) => {
  return `${apiEndPoints.applications}/${applicationId}/decision/editDecisionLeadingToCLR/${decisionId}`;
};

export const xhrFormat = (url: string): string => {
  return url.replace(/^\/+/, '');
};

export const uploadLetterEndPoint = (applicationId: string, taskId: number) => {
  return `/${apiEndPoints.applications}/${applicationId}/task/${taskId}/letter`;
};
export const uploadAttachmentEndPoint = (
  applicationId: string,
  taskId: number,
) => {
  return `/${apiEndPoints.applications}/${applicationId}/task/${taskId}/attachment`;
};
export const uploadPIAReportEndPoint = () => {
  return `/${apiEndPoints.cleanPia}`;
};
export const uploadPlanFEndPoint = () => {
  return `/${apiEndPoints.planFUpload}`;
};
export const listPlanFUploadsEndPoint = () => {
  return apiEndPoints.planFUploads;
};
export const planFCsvUrlEndPoint = (historyId: number, download = false) => {
  return `${apiEndPoints.planFUploads}/${historyId}/csv-url${
    download ? '?download=true' : ''
  }`;
};
export const uploadDigitalOwlEndPoint = () => {
  return `/${apiEndPoints.digitalOwlUpload}`;
};
export const getDigitalOwlUploadsEndPoint = () => {
  return apiEndPoints.digitalOwlUploads;
};
export const getDigitalOwlResultEndPoint = (jobId: number) => {
  return `admin/job/digitalOwl/${jobId}/result`;
};
export const evidenceRetryEndPoint = (
  applicationId: string,
  taskId: number,
) => {
  return `${apiEndPoints.applications}/${applicationId}/task/${taskId}/evidence/retry`;
};

export const carrierEligibilityRetryEndPoint = (
  applicationId: string,
  taskId: number,
) => {
  return `${apiEndPoints.applications}/${applicationId}/task/${taskId}/carrierEligibility/retry`;
};

export const taskByTaskTypeEndPoint = (
  applicationId: string,
  taskType: TaskTypes,
) => {
  return `${apiEndPoints.applications}/${applicationId}/task/${taskType}`;
};

export const automatedEvidencesEndPoint = (applicationId: string) => {
  return `${apiEndPoints.automatedEvidences}/${apiEndPoints.applications}/${applicationId}`;
};

export const automatedEvidencesUrl = (key: string) => {
  return `${apiEndPoints.automatedEvidences}/key/query?key=${encodeURIComponent(key)}`;
};

export const doctorsEndpoint = (applicationId: string, years?: number) => {
  const base = `${apiEndPoints.automatedEvidences}/doctors/${apiEndPoints.applications}/${applicationId}`;
  return years ? `${base}?years=${years}` : base;
};

export const getPolicyByUserAndRole = (id: number, queryString: string) => {
  return `${apiEndPoints.admin}/user/${id}/policy?${queryString}`;
};

export const enableUserEndPoint = (id: number) => {
  return `${apiEndPoints.admin}/user/${id}/enable`;
};

export const disableUserEndPoint = (id: number) => {
  return `${apiEndPoints.admin}/user/${id}/disable`;
};

export const pauseUserEndPoint = (id: number) => {
  return `${apiEndPoints.admin}/user/${id}/pause`;
};

export const unpauseUserEndPoint = (id: number) => {
  return `${apiEndPoints.admin}/user/${id}/unpause`;
};

export const manualPiaEndPoint = (id: number) => {
  return `${apiEndPoints.applications}/${id}/pia`;
};

export const initiateReinstatementEndPoint = (id: number) => {
  return `${apiEndPoints.applications}/${id}/reinstatement`;
};

export const getPolicyStatusEndPoint = (id: number): string => {
  return `${apiEndPoints.applications}/${id}/policy/status`;
};

export const validateMIBCodesEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/codeback/validate`;
};

export const getManualRuleResultsEndPoint = (applicationId: string): string => {
  return `${apiEndPoints.applications}/${applicationId}/decision/evaluatePIARules`;
};

export const deleteAttachmentEndpoint = (
  taskId: number,
  taskType: TaskTypes,
  attachmentId: number,
): string => {
  return `${apiEndPoints.attachment}/${taskId}/${taskType}/delete/${attachmentId}`;
};

export const getUnderwriterOverviewEndPoint = (id: number): string => {
  return `${apiEndPoints.applications}/underwriter-overview/?id=${id}`;
};

export const getUnderwritersEndPoint = (): string => {
  return `${apiEndPoints.underwriters}`;
};

/**
 * Assignee-list URL for the Bulk Case Assignment dropdown.
 * Returns active, non-paused users for the chosen role, sorted alphabetically.
 */
export const getAssignableUsersEndPoint = (
  role: 'underwriter' | 'casemanager',
): string => {
  const base =
    role === 'underwriter'
      ? apiEndPoints.underwriters
      : apiEndPoints.caseManagers;
  return `${base}?paused=false&sortBy=name`;
};

export const getUnderwriterApplicationsByTypeEndPoint = (
  userId: number,
  type: string,
  page: number,
  sortBy: string,
  sortOrder: string,
): string => {
  return `${apiEndPoints.applications}/underwriter-applications-list?userId=${userId}&type=${type}&page=${page}&sort_by=${sortBy}&sort_order=${sortOrder}`;
};

export const getETIEndPoint = (taskId: number) => {
  return `${apiEndPoints.eti}/task/${taskId}`;
};

export const getETIDocumentEndPoint = (key: string) => {
  return `${apiEndPoints.document}/key/query?key=${key}`;
};

export const getETISurveyJSJsonEndPoint = (taskId: number) => {
  return `${apiEndPoints.eti}/task/${taskId}/surveyjs`;
};

export const sendEmailEndPoint = (
  applicationId: string,
  taskId: number,
  termPolicyId: number,
) => {
  return `${apiEndPoints.applications}/${applicationId}/task/${taskId}/policy/${termPolicyId}/send-email`;
};

export const getPolicyQuestions = (policyId: number): string => {
  return `${apiEndPoints.applications}/${policyId}/interviewExport`;
};

export const getETIFileDownloadEndPoint = (
  policyId: number,
  taskId: number,
  fileUuid: string,
) => {
  return `${apiEndPoints.eti}/policy/${policyId}/task/${taskId}/file/${fileUuid}`;
};

export const manualTriggerClrEndPoint = (applicationId: string) => {
  return `${apiEndPoints.applications}/${applicationId}/manualTriggerClr`;
};

export const disputeResolvedEndPoint = (id: string): string => {
  return `${apiEndPoints.applications}/${id}/dispute-resolved`;
};

export const getDisclosureFactsEndPoint = (applicationId: number): string => {
  return `${apiEndPoints.applications}/${applicationId}/disclosure-facts`;
};

export const autoMedicalClaimsEndpoint = (evidenceId: number) => {
  return `${apiEndPoints.automatedEvidences}/${evidenceId}/autoMedicalClaims`;
};

export const mvrSupplementalEndpoint = (evidenceId: number) => {
  return `${apiEndPoints.automatedEvidences}/${evidenceId}/mvrSupplemental`;
};
